import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../data";
import { useAction } from "../../components/ui";
import { ApiError } from "../../data/api";
import { ENTERPRISE_GRENZEN, eingabeAusStand, geaenderteGrenzen, grenzeText, type EnterpriseStand } from "./enterprise-model";

export const enterpriseStandKey = (orgId: string) => ["platform_enterprise_limits", orgId] as const;
export function useEnterpriseStand(orgId: string, aktiv = true) {
  return useQuery({
    queryKey: enterpriseStandKey(orgId), enabled: aktiv, staleTime: 30_000,
    queryFn: () => api.read<EnterpriseStand>("platform_enterprise_limits", { p_org: orgId }),
  });
}

/**
 * Enterprise-Vertrag (0541, Befund S4): eigene Grenzen je Verbund. Gilt an der Hauptagentur für alle Standorte, nur im Tarif
 * Enterprise. Leer heißt Standard des Tarifs; Bestehendes bleibt auch bei einer kleineren Grenze erhalten, nur Neuanlegen stoppt.
 */
export function EnterpriseGrenzen({ orgId }: { orgId: string }) {
  const stand = useEnterpriseStand(orgId);
  const qc = useQueryClient();
  const { busy, error, run } = useAction();
  const [eingaben, setEingaben] = useState<Record<string, string> | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  if (stand.isPending || stand.error || !stand.data?.enterprise) return null;
  const s = stand.data;
  const werte = eingaben ?? Object.fromEntries(ENTERPRISE_GRENZEN.map((g) => [g.key, eingabeAusStand(s, g.key)]));
  async function speichern() {
    const d = geaenderteGrenzen(s, werte);
    if ("fehler" in d) { setFehler(d.fehler); return; }
    setFehler(null);
    if (!d.length) throw new ApiError("Es gibt nichts zu speichern.");
    for (const x of d) await api.rpc("platform_enterprise_limit_set", { p_org: orgId, p_key: x.key, p_value: x.value });
    setEingaben(null);
    await qc.invalidateQueries({ queryKey: enterpriseStandKey(orgId) });
  }
  return (
    <section className="panel stack" aria-labelledby="enterprise-titel">
      <h2 id="enterprise-titel">Enterprise-Grenzen</h2>
      <p>
        {s.is_billing ? "Gelten für den ganzen Verbund als Summe aller Standorte." : `Gelten für den ganzen Verbund und werden an der Hauptagentur ${s.billing_org_name ?? ""} geführt.`}{" "}
        Leer lassen heißt Standard des Tarifs; „unbegrenzt“ hebt die Grenze auf. Abgerechnet wird nach Vertrag, nicht über Stripe.
      </p>
      <div className="tk-felder">
        {ENTERPRISE_GRENZEN.map((g) => (
          <label key={g.key}>
            {g.label}{g.einheit ? ` (${g.einheit})` : ""}
            <input inputMode="numeric" value={werte[g.key] ?? ""} placeholder={`Standard: ${grenzeText(s.defaults[g.key])}`} aria-describedby={`ent-${g.key}`}
              onChange={(e) => setEingaben({ ...werte, [g.key]: e.target.value })} />
            <small id={`ent-${g.key}`}>Wirksam: {grenzeText(s.effective[g.key], g.einheit)}</small>
          </label>
        ))}
      </div>
      <div className="row">
        <button type="button" className="primary" disabled={!!busy} aria-busy={busy === "ent"} onClick={() => void run("ent", speichern, "Enterprise-Grenzen gespeichert.")}>
          {busy === "ent" ? "Wird gespeichert …" : "Grenzen speichern"}
        </button>
        {eingaben && <button type="button" disabled={!!busy} onClick={() => { setEingaben(null); setFehler(null); }}>Verwerfen</button>}
      </div>
      {(fehler ?? error) && <p className="tk-error" role="alert">{fehler ?? error}</p>}
    </section>
  );
}
